'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import {
    decideVersionAction,
    reloadGuardKey,
    trackVersionSighting,
    type VersionSighting,
} from './app-version.util'
import { hasUnsavedWork } from './unsaved-work'

/** Versão desta compilação, injetada pelo Vite (`vite.config.ts`). */
declare const __APP_BUILD_ID__: string

/**
 * Backstop de tempo: a aba que fica à vista e parada o dia inteiro (televisão
 * da recepção, segundo monitor) nunca dispara foco nem reconexão. Quinze
 * minutos é raro o bastante para não pesar — o arquivo tem 60 bytes.
 */
const POLL_INTERVAL_MS = 15 * 60 * 1000

/** Não checa de novo se acabou de checar: foco e reconexão costumam vir juntos. */
const MIN_INTERVAL_BETWEEN_CHECKS_MS = 30_000

/**
 * Quanto a versão nova precisa se MANTER antes de valer um recarregamento.
 *
 * O deploy é escalonado (`serial: 1` no playbook): medido em 30/09/2026, as duas VMs
 * de API subiram com 2 MINUTOS de diferença. Nessa janela elas servem versões
 * diferentes — recarregar na primeira detecção pode levar o usuário de volta à versão
 * antiga da outra VM, ou pior, a uma VM ainda reiniciando.
 *
 * Foi o que aconteceu no primeiro deploy com esta funcionalidade no ar: parte da
 * equipe relatou "o sistema caiu" enquanto para outros nada aconteceu. A atualização
 * automática não causou a instabilidade do deploy, mas jogou as pessoas dentro dela.
 *
 * Confirmar a mesma versão depois de 3 minutos põe o recarregamento DEPOIS do fim do
 * rodízio. Atualizar 3 minutos mais tarde não custa nada; recarregar no meio do deploy
 * custa a confiança de quem está atendendo.
 *
 * ⚠️ **A tarja espera a mesma confirmação.** Na primeira versão deste código ela
 * aparecia na hora, com o argumento de que quem quisesse atualizar já clicaria. Mas a
 * tarja É o convite para clicar: mostrada no meio do rodízio, ela leva a pessoa a
 * recarregar exatamente no pior momento — e cair na VM que ainda não subiu, ou numa
 * que está reiniciando. Relatado em 01/10/2026, com o deploy manual ainda em curso.
 */
const VERSION_STABLE_AFTER_MS = 180_000

async function fetchServerVersion(): Promise<string | null> {
    try {
        // `cache: 'no-store'` é o que faz este pedido dizer a verdade — sem ele o
        // navegador serve a própria cópia e a versão nunca muda.
        const response = await fetch('/version.json', { cache: 'no-store' })
        if (!response.ok) return null
        const data = await response.json()
        return typeof data?.buildId === 'string' ? data.buildId : null
    } catch {
        // Sem rede ou servidor reiniciando (o deploy!): tenta na próxima.
        return null
    }
}

function alreadyReloadedFor(version: string): boolean {
    try {
        return sessionStorage.getItem(reloadGuardKey(version)) === '1'
    } catch {
        // Aba anônima ou armazenamento bloqueado: sem trava, e aí é melhor NÃO
        // recarregar sozinho do que arriscar laço infinito na cara do usuário.
        return true
    }
}

function markReloadedFor(version: string): void {
    try {
        sessionStorage.setItem(reloadGuardKey(version), '1')
    } catch {
        // Ignorado de propósito: ver acima.
    }
}

/**
 * Descobre que subiu versão nova e recarrega na hora certa.
 *
 * Os dois gatilhos são de evento, não de relógio: a aba voltar ao foco e o
 * socket reconectar. O segundo é preciso — o backend reinicia no deploy, então
 * toda reconexão é um forte indício de que acabou de subir versão.
 *
 * Devolve `updateAvailable` para a tarja poder oferecer "Atualizar agora" quando
 * a decisão foi esperar.
 */
export function useAppVersion(): { updateAvailable: boolean; reloadNow: () => void } {
    const [updateAvailable, setUpdateAvailable] = useState(false)
    const lastCheckRef = useRef(0)
    const pendingReloadRef = useRef<number | null>(null)
    /** Quando esta versão do servidor foi vista pela PRIMEIRA vez. Ver `VERSION_STABLE_AFTER_MS`. */
    const firstSeenRef = useRef<VersionSighting>({ version: null, at: 0 })

    const reloadNow = useCallback(() => {
        window.location.reload()
    }, [])

    const check = useCallback(async () => {
        const now = Date.now()
        if (now - lastCheckRef.current < MIN_INTERVAL_BETWEEN_CHECKS_MS) return
        lastCheckRef.current = now

        const server = await fetchServerVersion()

        // Antes de qualquer decisão: a versão nova se firmou, ou o rodízio do deploy
        // ainda está em curso? `trackVersionSighting` também reinicia o relógio quando
        // o servidor volta a responder a NOSSA versão (uma VM subiu, a outra não).
        const previousSighting = firstSeenRef.current
        const { sighting, confirmed } = trackVersionSighting(
            previousSighting,
            server,
            __APP_BUILD_ID__,
            now,
            VERSION_STABLE_AFTER_MS,
        )
        firstSeenRef.current = sighting

        const action = decideVersionAction({
            current: __APP_BUILD_ID__,
            server,
            visible: document.visibilityState === 'visible',
            hasUnsavedWork: hasUnsavedWork(),
            alreadyReloadedFor,
        })

        if (action.kind === 'none') return

        if (!confirmed) {
            // Primeira vez que se vê esta versão: reexame agendado. Sem ele a
            // confirmação ficaria esperando o próximo foco ou a varredura de 15
            // minutos, e a aba parada nunca atualizaria.
            if (sighting.version !== previousSighting.version) {
                window.setTimeout(() => void check(), VERSION_STABLE_AFTER_MS + 1_000)
            }
            return
        }

        // Versão confirmada: agora sim a tarja pode convidar ao clique.
        setUpdateAvailable(true)
        if (action.kind === 'notify') return

        markReloadedFor(server!)
        if (pendingReloadRef.current !== null) return
        pendingReloadRef.current = window.setTimeout(reloadNow, action.delayMs)
    }, [reloadNow])

    useEffect(() => {
        void check()

        const onVisible = () => {
            if (document.visibilityState === 'visible') void check()
        }
        document.addEventListener('visibilitychange', onVisible)
        window.addEventListener('focus', onVisible)

        // Reconexão do socket: o backend reiniciou, provavelmente por deploy.
        const onSocketReconnect = () => void check()
        window.addEventListener('app:socket-reconnected', onSocketReconnect)

        const interval = window.setInterval(() => void check(), POLL_INTERVAL_MS)

        return () => {
            document.removeEventListener('visibilitychange', onVisible)
            window.removeEventListener('focus', onVisible)
            window.removeEventListener('app:socket-reconnected', onSocketReconnect)
            window.clearInterval(interval)
            if (pendingReloadRef.current !== null) window.clearTimeout(pendingReloadRef.current)
        }
    }, [check])

    return { updateAvailable, reloadNow }
}
